import { Volume2, X } from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { savedVoiceSelections, voiceCastFromSelections, voicePreviewUrl, voiceProfiles, voicesForLanguage, type VoiceProfile } from '../lib/voices';
import type { MeetingLanguage, MeetingParticipant, VoiceCast } from '../types';

export default function VoiceCastDialog({
  roster,
  language,
  initialCast,
  launching,
  onCancel,
  onStart,
}: {
  roster: MeetingParticipant[];
  language: MeetingLanguage;
  initialCast?: VoiceCast | null;
  launching: boolean;
  onCancel: () => void;
  onStart: (cast: VoiceCast) => void;
}) {
  const initialSelections = useMemo(() => savedVoiceSelections(roster, language, initialCast), [initialCast, language, roster]);
  const [selections, setSelections] = useState(initialSelections);
  const [previewingParticipantId, setPreviewingParticipantId] = useState('');
  const dialogRef = useRef<HTMLElement>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const voices = voicesForLanguage(language);
  const simulatedParticipants = roster.filter((participant) => !participant.isUser).slice(0, 12);
  const cast = useMemo(() => voiceCastFromSelections(roster, language, selections), [language, roster, selections]);

  useEffect(() => setSelections(initialSelections), [initialSelections]);
  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.focus();
    return () => previousFocus?.focus();
  }, []);
  useEffect(() => () => {
    previewAudioRef.current?.pause();
    previewAudioRef.current = null;
  }, []);

  function stopPreview() {
    previewAudioRef.current?.pause();
    previewAudioRef.current = null;
    setPreviewingParticipantId('');
  }

  function playPreview(participantId: string, voice: VoiceProfile) {
    if (previewingParticipantId === participantId && previewAudioRef.current) {
      stopPreview();
      return;
    }
    stopPreview();
    const audio = new Audio(voicePreviewUrl(voice.id));
    previewAudioRef.current = audio;
    audio.addEventListener('ended', () => {
      if (previewAudioRef.current !== audio) return;
      previewAudioRef.current = null;
      setPreviewingParticipantId('');
    }, { once: true });
    audio.addEventListener('error', () => {
      if (previewAudioRef.current !== audio) return;
      previewAudioRef.current = null;
      setPreviewingParticipantId('');
    }, { once: true });
    void audio.play().then(() => {
      if (previewAudioRef.current === audio) setPreviewingParticipantId(participantId);
    }).catch(() => {
      if (previewAudioRef.current === audio) previewAudioRef.current = null;
      setPreviewingParticipantId('');
    });
  }

  function keepFocusInDialog(event: KeyboardEvent<HTMLElement>) {
    if (event.key === 'Escape' && !launching) {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])') || [])];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div className="voice-cast-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !launching) onCancel(); }}>
      <section ref={dialogRef} className="voice-cast-dialog" role="dialog" aria-modal="true" aria-labelledby="voice-cast-title" tabIndex={-1} onKeyDown={keepFocusInDialog}>
        <button type="button" className="voice-cast-close" aria-label="Close voice casting" disabled={launching} onClick={onCancel}><X size={20} /></button>
        <div className="voice-cast-heading">
          <h2 id="voice-cast-title">Cast meeting participants</h2>
          <p>Choose a voice for each simulated participant.</p>
        </div>

        <div className="voice-cast-list">
          {roster.filter((participant) => participant.isUser).map((participant) => (
            <div className="voice-cast-person user" key={participant.id}>
              <span className="voice-person-initial">{participant.name.charAt(0)}</span>
              <span><strong>{participant.name}</strong><small>{participant.role || 'You'}</small></span>
              <span className="voice-user-input">Your microphone</span>
            </div>
          ))}
          {simulatedParticipants.map((participant) => {
            const selected = voiceProfiles.find((voice) => voice.id === selections[participant.id]);
            return (
              <div className="voice-cast-person" key={participant.id}>
                <span className={`voice-person-initial ${selected?.presentation || ''}`}>{participant.name.charAt(0)}</span>
                <span><strong>{participant.name}</strong>{participant.role && <small>{participant.role}</small>}</span>
                <div className="voice-cast-picker">
                  <label>
                    <span className="sr-only">Voice for {participant.name}</span>
                    <select value={selections[participant.id] || ''} onChange={(event) => {
                      if (previewingParticipantId === participant.id) stopPreview();
                      setSelections((current) => ({ ...current, [participant.id]: event.target.value }));
                    }}>
                      <option value="">Choose a voice</option>
                      {voices.map((voice) => <option key={voice.id} value={voice.id}>{voice.name} · {voice.description}</option>)}
                    </select>
                  </label>
                  <button
                    type="button"
                    className={`voice-preview-button${previewingParticipantId === participant.id ? ' playing' : ''}`}
                    aria-label={selected ? `${previewingParticipantId === participant.id ? 'Stop' : 'Preview'} ${selected.name} sample for ${participant.name}` : `Choose a sample for ${participant.name}`}
                    aria-pressed={previewingParticipantId === participant.id}
                    title={selected ? `${previewingParticipantId === participant.id ? 'Stop' : 'Play'} ${selected.name} voice sample` : 'Choose a voice to hear a sample'}
                    disabled={launching || !selected}
                    onClick={() => { if (selected) playPreview(participant.id, selected); }}
                  >
                    <Volume2 size={18} />
                  </button>
                  <small>{selected ? `${selected.presentation}-presenting` : 'Required'}</small>
                </div>
              </div>
            );
          })}
        </div>

        <div className="voice-cast-actions">
          <button type="button" className="button secondary" disabled={launching} onClick={onCancel}>Cancel</button>
          <button type="button" className="button primary" disabled={launching || !cast} onClick={() => { if (cast) onStart(cast); }}>{launching ? 'Starting call...' : 'Start voice practice'}</button>
        </div>
      </section>
    </div>
  );
}
